import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import Section from '../base/Section';

interface Stat {
  icon: string;
  number: number;
  suffix: string;
  label: string;
}

export default function StatsSection() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
    rootMargin: '50px',
  });

  const stats: Stat[] = [
    { icon: 'ri-time-line', number: 10, suffix: '+', label: 'Años de experiencia' },
    { icon: 'ri-home-smile-line', number: 500, suffix: '+', label: 'Pisos vaciados' },
    { icon: 'ri-customer-service-2-line', number: 24, suffix: '/7', label: 'Servicio disponible' },
    { icon: 'ri-shield-check-line', number: 100, suffix: '%', label: 'Discreto y profesional' },
  ];

  return (
    <Section background="white" padding="lg">
      <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {stats.map((stat, index) => (
          <StatCard key={index} stat={stat} inView={inView} delay={index * 0.1} />
        ))}
      </div>
    </Section>
  );
}

function StatCard({ stat, inView, delay }: { stat: Stat; inView: boolean; delay: number }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (inView) {
      let start = 0;
      const end = stat.number;
      const duration = 2000;
      const increment = end / (duration / 16);
      let animationFrame: number;

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
          if (animationFrame) cancelAnimationFrame(animationFrame);
        } else {
          animationFrame = requestAnimationFrame(() => {
            setCount(Math.floor(start));
          });
        }
      }, 16);

      return () => {
        clearInterval(timer);
        if (animationFrame) cancelAnimationFrame(animationFrame);
      };
    }
  }, [inView, stat.number]);

  return (
    <div 
      className="text-center transform-gpu"
      style={{ 
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transition: `all 0.6s ease-out ${delay}s`,
        willChange: inView ? 'auto' : 'transform, opacity'
      }}
    >
      <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
        <i className={`${stat.icon} text-5xl text-tertiary`}></i>
      </div>
      <div className="text-6xl font-bold text-primary mb-2">
        {count}{stat.suffix}
      </div>
      <p className="text-lg text-secondary font-medium">{stat.label}</p>
    </div>
  );
}
